// The "more" (⋮) menu shared by the composer, the interactive VoicePills, and hands-free.
// Collapsed it shows compact rows: Captions (toggle), Hands-free (action, when offered), Speaker and
// Mic. Speaker/Mic EXPAND on click to reveal their device list; CC and Hands-free are simple rows.
// Output-device switching is Chromium-desktop only — elsewhere the speaker section shows a short note.
// When voicePrefs + onUpdateVoicePrefs are provided a "Voice quality" section appears at the bottom:
//   • Noise reduction (off / near-field / far-field)
//   • Turn detection (Standard server_vad / Smart semantic_vad)
//   • IF server_vad: VAD threshold slider + silence-duration slider
//   • IF semantic_vad: eagerness selector (low / medium / high / auto)
//
// The popup is rendered via a React portal (document.body, position:fixed) so that any ancestor
// overflow:hidden in the composer bar cannot clip it horizontally on mobile.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreIcon, CheckIcon, CaptionIcon, MicIcon, SpeakerIcon, HandsFreeIcon, ChevronDownIcon } from '@/components/chat/ChatIcons';
import type { AudioDevice } from '@/hooks/useAudioDevices';
import type { VoicePrefs } from '@/redux/internal-services/agentApi';

export interface AudioMoreMenuProps {
  captionsOn: boolean;
  onToggleCaptions: () => void;
  inputs: AudioDevice[];
  outputs: AudioDevice[];
  outputSupported: boolean;
  /** Selected device ids ('' = system default). */
  inputId: string;
  outputId: string;
  onSelectInput: (id: string) => void;
  onSelectOutput: (id: string) => void;
  /** Called when the menu opens — refresh the device list / labels. */
  onOpen?: () => void;
  /** Where the popover aligns relative to the trigger. */
  align?: 'left' | 'right';
  /** When provided, a "Hands-free" row enters hands-free mode (composer, mobile only). */
  onEnterHandsFree?: () => void;
  /** Override the trigger button's size/surface (e.g. to match a tall End button in hands-free). */
  triggerClassName?: string;
  /** When provided, show the voice-quality settings section. */
  voicePrefs?: VoicePrefs;
  onUpdateVoicePrefs?: (patch: Partial<VoicePrefs>) => void;
}

const POPUP_WIDTH = 288; // w-72
const GAP = 8;

const DEFAULT_DEVICE: AudioDevice = { deviceId: '', label: 'System default' };

const deviceLabel = (list: AudioDevice[], id: string): string =>
  id ? (list.find((d) => d.deviceId === id)?.label || 'Selected device') : 'System default';

const DeviceRow = ({ device, selected, onClick }: { device: AudioDevice; selected: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between gap-2 rounded-lg pl-9 pr-3 py-1.5 text-left text-sm transition ${
      selected ? 'text-ui-text font-medium' : 'text-ui-text2 hover:bg-ui-hover'
    }`}
  >
    <span className="truncate">{device.label}</span>
    {selected && <CheckIcon className="h-4 w-4 flex-shrink-0 text-accent-text" />}
  </button>
);

const WaveIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
  </svg>
);

const ChipGroup = ({
  value,
  options,
  onChange,
}: {
  value: string | null;
  options: { value: string | null; label: string }[];
  onChange: (v: string | null) => void;
}) => (
  <div className="flex flex-wrap gap-1 mt-1 mb-1">
    {options.map((o) => (
      <button
        key={String(o.value)}
        onClick={() => onChange(o.value)}
        className={`px-2.5 py-1 rounded-full text-xs font-medium transition border ${
          value === o.value
            ? 'bg-accent-fill text-on-accent border-accent'
            : 'text-ui-text2 border-ui-border hover:bg-ui-hover'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const SliderRow = ({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
}) => (
  <div className="mt-2">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs text-ui-text2">{label}</span>
      <span className="text-xs font-medium text-ui-text">{format(value)}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-accent-fill h-1.5 rounded-full cursor-pointer"
    />
    <div className="flex justify-between mt-0.5">
      <span className="text-xs text-ui-text3">{format(min)}</span>
      <span className="text-xs text-ui-text3">{format(max)}</span>
    </div>
  </div>
);

const VoiceQualityPanel = ({
  prefs,
  onUpdate,
}: {
  prefs: VoicePrefs;
  onUpdate: (patch: Partial<VoicePrefs>) => void;
}) => {
  const isServer = prefs.turn_detection_type === 'server_vad';

  return (
    <div className="px-3 pt-2 pb-3 border-t border-ui-border mt-1">
      <span className="text-micro font-semibold uppercase tracking-wider text-ui-text3">Voice quality</span>

      <div className="mt-2">
        <span className="text-xs text-ui-text2">noise_reduction</span>
        <ChipGroup
          value={prefs.noise_reduction}
          options={[
            { value: null, label: 'off' },
            { value: 'near_field', label: 'near_field' },
            { value: 'far_field', label: 'far_field' },
          ]}
          onChange={(v) => onUpdate({ noise_reduction: v as VoicePrefs['noise_reduction'] })}
        />
      </div>

      <div className="mt-3">
        <span className="text-xs text-ui-text2">turn_detection_type</span>
        <ChipGroup
          value={prefs.turn_detection_type}
          options={[
            { value: 'server_vad', label: 'server_vad' },
            { value: 'semantic_vad', label: 'semantic_vad' },
          ]}
          onChange={(v) => onUpdate({ turn_detection_type: v as VoicePrefs['turn_detection_type'] })}
        />
      </div>

      {isServer && (
        <>
          <SliderRow
            label="vad_threshold"
            value={prefs.vad_threshold}
            min={0.5}
            max={1.0}
            step={0.05}
            format={(v) => v.toFixed(2)}
            onChange={(v) => onUpdate({ vad_threshold: v })}
          />
          <SliderRow
            label="silence_duration_ms"
            value={prefs.silence_duration_ms}
            min={300}
            max={2000}
            step={100}
            format={(v) => `${v}`}
            onChange={(v) => onUpdate({ silence_duration_ms: v })}
          />
        </>
      )}

      {!isServer && (
        <div className="mt-3">
          <span className="text-xs text-ui-text2">semantic_vad_eagerness</span>
          <ChipGroup
            value={prefs.semantic_vad_eagerness}
            options={[
              { value: 'low', label: 'low' },
              { value: 'medium', label: 'medium' },
              { value: 'high', label: 'high' },
              { value: 'auto', label: 'auto' },
            ]}
            onChange={(v) => onUpdate({ semantic_vad_eagerness: v as VoicePrefs['semantic_vad_eagerness'] })}
          />
        </div>
      )}
    </div>
  );
};

export const AudioMoreMenu = ({
  captionsOn,
  onToggleCaptions,
  inputs,
  outputs,
  outputSupported,
  inputId,
  outputId,
  onSelectInput,
  onSelectOutput,
  onOpen,
  align = 'right',
  onEnterHandsFree,
  triggerClassName,
  voicePrefs,
  onUpdateVoicePrefs,
}: AudioMoreMenuProps) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<'mic' | 'speaker' | 'quality' | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Calculate the popup's fixed position from the trigger's viewport rect.
  // Because the popup is a portal child of <body>, position:fixed values map directly
  // to getBoundingClientRect() coordinates — no ancestor overflow can clip it.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !popoverRef.current) return;
    const tr = triggerRef.current.getBoundingClientRect();
    const viewW = document.documentElement.clientWidth;
    const viewH = window.innerHeight;

    // Vertical: above the trigger
    popoverRef.current.style.bottom = `${viewH - tr.top + GAP}px`;

    // Horizontal: prefer right-aligned to trigger; shift right if popup would overflow left edge
    const preferredLeft = (align === 'right' ? tr.right : tr.left) - (align === 'right' ? POPUP_WIDTH : 0);
    const clampedLeft = Math.min(Math.max(GAP, preferredLeft), viewW - POPUP_WIDTH - GAP);
    popoverRef.current.style.left = `${clampedLeft}px`;
    popoverRef.current.style.right = 'auto';
    popoverRef.current.style.visibility = 'visible';
  }, [open, expanded, align]);

  useEffect(() => {
    if (!open) {
      setExpanded(null);
      return;
    }
    onOpen?.();
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = triggerRef.current?.contains(target);
      const inPopover = popoverRef.current?.contains(target);
      if (!inTrigger && !inPopover) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const rowBtn = 'w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-ui-text hover:bg-ui-hover transition';

  const popup = open ? (
    <div
      ref={popoverRef}
      style={{ visibility: 'hidden' }}
      className="fixed z-[9999] w-72 max-h-[70dvh] overflow-y-auto no-scrollbar rounded-2xl border border-ui-border bg-ui-card shadow-xl p-1.5"
    >
      <button onClick={onToggleCaptions} className={rowBtn}>
        <span className="flex items-center gap-2"><CaptionIcon className="h-4 w-4" /> Captions</span>
        <span className={`text-xs font-medium ${captionsOn ? 'text-accent-text' : 'text-ui-text2'}`}>{captionsOn ? 'On' : 'Off'}</span>
      </button>

      {onEnterHandsFree && (
        <button onClick={() => { setOpen(false); onEnterHandsFree(); }} className={rowBtn}>
          <span className="flex items-center gap-2"><HandsFreeIcon className="h-4 w-4" /> Hands-free</span>
        </button>
      )}

      <button onClick={() => setExpanded((e) => (e === 'speaker' ? null : 'speaker'))} className={rowBtn}>
        <span className="flex items-center gap-2 min-w-0"><SpeakerIcon className="h-4 w-4 flex-shrink-0" /> Speaker</span>
        <span className="flex items-center gap-1 min-w-0 text-xs text-ui-text3">
          <span className="truncate max-w-[90px] text-ui-text2">{outputSupported ? deviceLabel(outputs, outputId) : 'System'}</span>
          <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${expanded === 'speaker' ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {expanded === 'speaker' && (
        outputSupported ? (
          [DEFAULT_DEVICE, ...outputs].map((d) => (
            <DeviceRow key={`out-${d.deviceId}`} device={d} selected={outputId === d.deviceId}
              onClick={() => { onSelectOutput(d.deviceId); setExpanded(null); }} />
          ))
        ) : (
          <p className="pl-9 pr-3 py-1.5 text-xs text-ui-text3">Your device controls audio output.</p>
        )
      )}

      <button onClick={() => setExpanded((e) => (e === 'mic' ? null : 'mic'))} className={rowBtn}>
        <span className="flex items-center gap-2 min-w-0"><MicIcon className="h-4 w-4 flex-shrink-0" /> Mic</span>
        <span className="flex items-center gap-1 min-w-0 text-xs text-ui-text3">
          <span className="truncate max-w-[90px] text-ui-text2">{deviceLabel(inputs, inputId)}</span>
          <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${expanded === 'mic' ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {expanded === 'mic' && (
        [DEFAULT_DEVICE, ...inputs].map((d) => (
          <DeviceRow key={`in-${d.deviceId}`} device={d} selected={inputId === d.deviceId}
            onClick={() => { onSelectInput(d.deviceId); setExpanded(null); }} />
        ))
      )}

      {voicePrefs && onUpdateVoicePrefs && (
        <>
          <button onClick={() => setExpanded((e) => (e === 'quality' ? null : 'quality'))} className={rowBtn}>
            <span className="flex items-center gap-2 min-w-0"><WaveIcon className="h-4 w-4 flex-shrink-0" /> Voice quality</span>
            <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${expanded === 'quality' ? 'rotate-180' : ''}`} />
          </button>
          {expanded === 'quality' && (
            <VoiceQualityPanel prefs={voicePrefs} onUpdate={onUpdateVoicePrefs} />
          )}
        </>
      )}
    </div>
  ) : null;

  return (
    <div ref={triggerRef} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        title="More"
        aria-label="More options"
        className={`rounded-full flex items-center justify-center transition btn-icon-hover ${triggerClassName || 'w-9 h-9'} ${
          open ? 'bg-ui-hover text-ui-text border-ui-border' : 'text-icon hover:bg-ui-hover'
        }`}
      >
        <MoreIcon className="h-5 w-5" />
      </button>

      {createPortal(popup, document.body)}
    </div>
  );
};

export default AudioMoreMenu;
